import Papa from 'papaparse';
import { getServiceName } from './serviceTranslations';

export const WALK_IN_CUSTOMER_OPTION_ID = '__walk_in__';

const HEADER_ALIASES = {
  reference: ['referência do agendamento', 'referencia do agendamento', 'appointment reference', 'reference'],
  customer: ['cliente', 'client', 'customer'],
  teamMember: ['colaborador', 'team member', 'employee', 'staff'],
  status: ['situação', 'situacao', 'status'],
  scheduledDate: ['data agendada', 'scheduled date', 'appointment date'],
  slot: ['horário do agendamento', 'horario do agendamento', 'appointment time', 'time'],
  category: ['categoria', 'category'],
  service: ['serviço', 'servico', 'service', 'service name'],
  duration: ['duração (minutos)', 'duracao (minutos)', 'duration', 'duration (minutes)'],
  netSales: ['vendas líquidas', 'vendas liquidas', 'net sales'],
  cancellationDate: ['data de cancelamento', 'cancellation date'],
  cancellationReason: ['motivo do cancelamento', 'cancellation reason'],
};

export const FRESHA_APPOINTMENT_STATUS_OPTIONS = [
  { value: 'Novo', labelKey: 'dataImport.appointments.status.new' },
  { value: 'Concluído', labelKey: 'dataImport.appointments.status.completed' },
  { value: 'Cancelado', labelKey: 'dataImport.appointments.status.cancelled' },
  { value: 'Não compareceu', labelKey: 'dataImport.appointments.status.noShow' },
];

const detectDelimiter = (text) => {
  const firstLine = (text.split(/\r?\n/).find((line) => {
    const trimmed = line.trim();
    return trimmed && !trimmed.toUpperCase().startsWith('SEP=');
  }) || '');
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return semicolons >= commas ? ';' : ',';
};

const normalizeWhitespace = (value) => (value == null ? '' : String(value).trim());

const collapseSpaces = (value) => normalizeWhitespace(value).replace(/\s+/g, ' ');

const normalizeHeader = (value) => normalizeWhitespace(value).toLowerCase();

const stripSepDirective = (text) => text.replace(/^\uFEFF?SEP=.+\r?\n/i, '');

const findHeaderIndex = (headers, aliases) => {
  const normalizedHeaders = headers.map(normalizeHeader);
  for (const alias of aliases) {
    const index = normalizedHeaders.indexOf(alias);
    if (index >= 0) return index;
  }
  return -1;
};

const buildHeaderMap = (headers) => {
  const map = {};
  Object.entries(HEADER_ALIASES).forEach(([key, aliases]) => {
    map[key] = findHeaderIndex(headers, aliases);
  });
  return map;
};

const getValue = (values, index) => (index >= 0 ? normalizeWhitespace(values[index]) : '');

const MONTHS = {
  jan: 0, january: 0,
  feb: 1, february: 1,
  mar: 2, march: 2,
  apr: 3, april: 3,
  may: 4,
  jun: 5, june: 5,
  jul: 6, july: 6,
  aug: 7, august: 7,
  sep: 8, sept: 8, september: 8,
  oct: 9, october: 9,
  nov: 10, november: 10,
  dec: 11, december: 11,
};

export const parseFreshaAppointmentDate = (rawValue) => {
  const raw = collapseSpaces(rawValue);
  if (!raw) {
    return { date: null, warningKey: null, original: '' };
  }

  const match = raw.match(
    /^(\d{1,2})\s+([A-Za-z]+)\s+(\d{4})(?:,\s*(\d{1,2}):(\d{2})\s*(am|pm))?$/i,
  );
  if (!match) {
    return { date: null, warningKey: null, original: raw };
  }

  const day = Number.parseInt(match[1], 10);
  const monthKey = match[2].toLowerCase();
  const year = Number.parseInt(match[3], 10);
  const month = MONTHS[monthKey];
  if (month == null) {
    return { date: null, warningKey: null, original: raw };
  }

  const date = new Date(Date.UTC(year, month, day));
  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month
    || date.getUTCDate() !== day
  ) {
    return { date: null, warningKey: null, original: raw };
  }

  const monthStr = String(month + 1).padStart(2, '0');
  const dayStr = String(day).padStart(2, '0');
  return {
    date: `${year}-${monthStr}-${dayStr}`,
    scheduledDateRaw: raw,
    warningKey: null,
    original: raw,
  };
};

export const parseFreshaAppointmentSlot = (rawValue) => {
  const raw = collapseSpaces(rawValue);
  if (!raw) {
    return { periodFrom: null, periodTo: null, original: '' };
  }

  const match = raw.match(/^(\d{1,2}:\d{2}(?::\d{2})?)\s*-\s*(\d{1,2}:\d{2}(?::\d{2})?)$/);
  if (!match) {
    return { periodFrom: null, periodTo: null, original: raw };
  }

  const normalizeTime = (value) => {
    const parts = value.split(':').map((part) => part.padStart(2, '0'));
    if (parts.length === 2) parts.push('00');
    return parts.join(':');
  };

  return {
    periodFrom: normalizeTime(match[1]),
    periodTo: normalizeTime(match[2]),
    original: raw,
  };
};

export const parseFreshaAppointmentStatus = (rawValue) => {
  const raw = collapseSpaces(rawValue);
  if (!raw) {
    return { status: '', known: false, original: '' };
  }

  const normalized = raw.toLowerCase();
  if (normalized === 'novo' || normalized === 'new') {
    return { status: 'Novo', known: true, original: raw };
  }
  if (normalized === 'concluído' || normalized === 'concluido' || normalized === 'completed') {
    return { status: 'Concluído', known: true, original: raw };
  }
  if (normalized === 'cancelado' || normalized === 'cancelled' || normalized === 'canceled') {
    return { status: 'Cancelado', known: true, original: raw };
  }
  if (
    normalized === 'não compareceu'
    || normalized === 'nao compareceu'
    || normalized === 'no show'
    || normalized === 'no-show'
    || normalized === 'noshow'
  ) {
    return { status: 'Não compareceu', known: true, original: raw };
  }

  return { status: raw, known: false, original: raw };
};

const MAX_APPOINTMENT_PRICE = 99999;

export const parseFreshaAppointmentPrice = (rawValue) => {
  const raw = collapseSpaces(rawValue);
  if (!raw) {
    return { unitPrice: null, warningKey: null, original: '' };
  }

  let cleaned = raw.replace(/[€$\s]/g, '');
  if (!cleaned) {
    return { unitPrice: null, warningKey: null, original: raw };
  }

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  const lastSeparator = Math.max(lastDot, lastComma);

  if (lastSeparator >= 0) {
    const integerPart = cleaned.slice(0, lastSeparator).replace(/[.,]/g, '');
    const decimalPart = cleaned.slice(lastSeparator + 1).replace(/[.,]/g, '');
    cleaned = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  } else {
    cleaned = cleaned.replace(/[.,]/g, '');
  }

  if (!/^\d+(\.\d+)?$/.test(cleaned)) {
    return {
      unitPrice: null,
      warningKey: 'dataImport.appointments.warnings.invalidPrice',
      original: raw,
    };
  }

  const parsed = Number.parseFloat(cleaned);
  if (Number.isNaN(parsed) || parsed < 0 || parsed > MAX_APPOINTMENT_PRICE) {
    return {
      unitPrice: null,
      warningKey: 'dataImport.appointments.warnings.invalidPrice',
      original: raw,
    };
  }

  return { unitPrice: parsed, warningKey: null, original: raw };
};

const buildRowFromValues = (values, rowNumber, headerMap) => {
  const errors = [];
  const warnings = [];

  const freshaAppointmentReference = collapseSpaces(getValue(values, headerMap.reference));
  const customerName = collapseSpaces(getValue(values, headerMap.customer));
  const teamMemberName = collapseSpaces(getValue(values, headerMap.teamMember));
  const categoryName = collapseSpaces(getValue(values, headerMap.category));
  const serviceName = collapseSpaces(getValue(values, headerMap.service));
  const durationRaw = getValue(values, headerMap.duration);
  const cancellationReason = collapseSpaces(getValue(values, headerMap.cancellationReason));

  if (!freshaAppointmentReference) {
    errors.push('dataImport.appointments.errors.referenceRequired');
  }
  if (!customerName) {
    errors.push('dataImport.appointments.errors.customerRequired');
  }
  if (!teamMemberName) {
    errors.push('dataImport.appointments.errors.teamMemberRequired');
  }
  if (!serviceName) {
    errors.push('dataImport.appointments.errors.serviceRequired');
  }

  const statusResult = parseFreshaAppointmentStatus(getValue(values, headerMap.status));
  if (!statusResult.status) {
    errors.push('dataImport.appointments.errors.statusRequired');
  } else if (!statusResult.known) {
    errors.push('dataImport.appointments.errors.statusUnknown');
  }

  const dateResult = parseFreshaAppointmentDate(getValue(values, headerMap.scheduledDate));
  if (!dateResult.date) {
    errors.push('dataImport.appointments.errors.dateRequired');
  }

  const slotResult = parseFreshaAppointmentSlot(getValue(values, headerMap.slot));
  if (!slotResult.periodFrom || !slotResult.periodTo) {
    errors.push('dataImport.appointments.errors.slotRequired');
  }

  const priceResult = parseFreshaAppointmentPrice(getValue(values, headerMap.netSales));
  if (priceResult.warningKey) {
    warnings.push(priceResult.warningKey);
  }

  if (durationRaw && !/^\d/.test(durationRaw) && !/\d/.test(durationRaw)) {
    warnings.push('dataImport.appointments.warnings.invalidDuration');
  }

  const customerRaw = getValue(values, headerMap.customer);
  const serviceRaw = getValue(values, headerMap.service);
  if (customerRaw !== customerName || serviceRaw !== serviceName) {
    warnings.push('dataImport.appointments.warnings.trailingSpaces');
  }

  return {
    rowNumber,
    freshaAppointmentReference,
    customerName,
    teamMemberName,
    categoryName,
    serviceName,
    status: statusResult.status,
    scheduledDate: dateResult.date,
    scheduledDateRaw: dateResult.original || getValue(values, headerMap.scheduledDate),
    periodFrom: slotResult.periodFrom,
    periodTo: slotResult.periodTo,
    unitPrice: priceResult.unitPrice,
    cancellationReason,
    durationRaw,
    errors,
    warnings,
    rawValues: values,
  };
};

export const normalizeNameKey = (value) => collapseSpaces(value).toUpperCase();

const findUniqueByName = (items, name, getName) => {
  const key = normalizeNameKey(name);
  if (!key) return null;
  const matches = (items || []).filter((item) => normalizeNameKey(getName(item)) === key);
  return matches.length === 1 ? matches[0] : null;
};

const getServiceNameKeys = (service, language) => {
  const keys = new Set();
  if (service?.name && typeof service.name === 'object') {
    Object.values(service.name).forEach((value) => {
      const key = normalizeNameKey(value);
      if (key) keys.add(key);
    });
  }
  const displayName = getServiceName(service?.name, language);
  const displayKey = normalizeNameKey(displayName);
  if (displayKey && displayName !== 'Unnamed Service') {
    keys.add(displayKey);
  }
  return keys;
};

const findUniqueService = (services, serviceName, categoryName, language) => {
  const nameKey = normalizeNameKey(serviceName);
  if (!nameKey) return null;

  const byName = (services || []).filter((service) => getServiceNameKeys(service, language).has(nameKey));
  if (byName.length === 0) return null;

  const categoryKey = normalizeNameKey(categoryName);
  if (categoryKey) {
    const byCategory = byName.filter((service) => {
      const categories = service.categories || service.categoryNames || [];
      if (!Array.isArray(categories) || categories.length === 0) {
        return normalizeNameKey(service.categoryName) === categoryKey;
      }
      return categories.some((category) => {
        if (typeof category === 'string') {
          return normalizeNameKey(category) === categoryKey;
        }
        return normalizeNameKey(category?.name) === categoryKey
          || getServiceNameKeys({ name: category?.name }, language).has(categoryKey);
      });
    });
    if (byCategory.length === 1) return byCategory[0];
    if (byCategory.length > 1) return null;
  }

  return byName.length === 1 ? byName[0] : null;
};

export const revalidateAppointmentRow = (row, catalogs = null) => {
  if (row.skipped) {
    return {
      ...row,
      skipped: true,
      errors: [],
      warnings: row.warnings || [],
    };
  }

  const draft = {
    freshaAppointmentReference: collapseSpaces(row.freshaAppointmentReference),
    customerName: collapseSpaces(row.customerName),
    teamMemberName: collapseSpaces(row.teamMemberName),
    categoryName: collapseSpaces(row.categoryName),
    serviceName: collapseSpaces(row.serviceName),
    status: collapseSpaces(row.status),
    scheduledDateRaw: collapseSpaces(row.scheduledDateRaw || row.scheduledDate),
    periodFrom: collapseSpaces(row.periodFrom),
    periodTo: collapseSpaces(row.periodTo),
    cancellationReason: collapseSpaces(row.cancellationReason),
  };

  const priceResult = typeof row.unitPrice === 'number'
    ? (
      row.unitPrice < 0 || row.unitPrice > MAX_APPOINTMENT_PRICE
        ? { unitPrice: null, warningKey: 'dataImport.appointments.warnings.invalidPrice' }
        : { unitPrice: row.unitPrice, warningKey: null }
    )
    : parseFreshaAppointmentPrice(row.unitPrice);
  draft.unitPrice = priceResult.unitPrice;

  const errors = [];
  const warnings = [];

  if (!draft.freshaAppointmentReference) {
    errors.push('dataImport.appointments.errors.referenceRequired');
  }
  if (!draft.customerName && !row.useWalkInCustomer && !row.resolvedCustomerId) {
    errors.push('dataImport.appointments.errors.customerRequired');
  }
  if (!draft.teamMemberName && !row.resolvedTeamMemberId) {
    errors.push('dataImport.appointments.errors.teamMemberRequired');
  }
  if (!draft.serviceName && !row.resolvedServiceId) {
    errors.push('dataImport.appointments.errors.serviceRequired');
  }

  const statusResult = parseFreshaAppointmentStatus(draft.status);
  if (!statusResult.status) {
    errors.push('dataImport.appointments.errors.statusRequired');
  } else if (!statusResult.known) {
    errors.push('dataImport.appointments.errors.statusUnknown');
  }

  const dateResult = parseFreshaAppointmentDate(draft.scheduledDateRaw);
  if (!dateResult.date) {
    errors.push('dataImport.appointments.errors.dateRequired');
  }

  const slotResult = parseFreshaAppointmentSlot(`${draft.periodFrom}-${draft.periodTo}`);
  if (!slotResult.periodFrom || !slotResult.periodTo) {
    errors.push('dataImport.appointments.errors.slotRequired');
  }

  if (priceResult.warningKey) {
    warnings.push(priceResult.warningKey);
  }

  let useWalkInCustomer = Boolean(row.useWalkInCustomer);
  let resolvedCustomerId = useWalkInCustomer ? null : (row.resolvedCustomerId || null);
  let resolvedServiceId = row.resolvedServiceId || null;
  let resolvedTeamMemberId = row.resolvedTeamMemberId || null;

  if (catalogs) {
    if (!useWalkInCustomer && !resolvedCustomerId) {
      const matchedCustomer = findUniqueByName(catalogs.customers, draft.customerName, (item) => item.name);
      resolvedCustomerId = matchedCustomer?.customerId || null;
    }
    if (!resolvedServiceId) {
      const matchedService = findUniqueService(
        catalogs.services,
        draft.serviceName,
        draft.categoryName,
        catalogs.language,
      );
      resolvedServiceId = matchedService?.id ?? null;
    }
    if (!resolvedTeamMemberId) {
      const matchedTeamMember = findUniqueByName(catalogs.teamMembers, draft.teamMemberName, (item) => item.name);
      resolvedTeamMemberId = matchedTeamMember?.id || null;
    }

    if (!useWalkInCustomer && !resolvedCustomerId) {
      errors.push('dataImport.appointments.errors.customerNotFound');
    }
    if (!resolvedServiceId) {
      errors.push('dataImport.appointments.errors.serviceNotFound');
    }
    if (!resolvedTeamMemberId) {
      errors.push('dataImport.appointments.errors.teamMemberNotFound');
    }
  }

  return {
    ...row,
    ...draft,
    status: statusResult.status || draft.status,
    scheduledDate: dateResult.date,
    scheduledDateRaw: draft.scheduledDateRaw,
    periodFrom: slotResult.periodFrom || draft.periodFrom,
    periodTo: slotResult.periodTo || draft.periodTo,
    useWalkInCustomer,
    resolvedCustomerId,
    resolvedServiceId,
    resolvedTeamMemberId,
    skipped: false,
    errors,
    warnings,
  };
};

export const resolveAppointmentRowsWithCatalogs = (rows, catalogs) => (
  (rows || []).map((row) => revalidateAppointmentRow(row, catalogs))
);

export const parseFreshaAppointmentCsv = (text) => {
  const cleaned = stripSepDirective(text || '');
  if (!normalizeWhitespace(cleaned)) {
    return { errorKey: 'dataImport.appointments.errors.emptyFile', rows: [], headers: [], delimiter: ',' };
  }

  const delimiter = detectDelimiter(cleaned);
  const parsed = Papa.parse(cleaned, {
    delimiter,
    skipEmptyLines: 'greedy',
  });

  if (!parsed.data?.length) {
    return { errorKey: 'dataImport.appointments.errors.emptyFile', rows: [], headers: [], delimiter };
  }

  const headers = (parsed.data[0] || []).map((value) => normalizeWhitespace(value));
  const headerMap = buildHeaderMap(headers);

  if (
    headerMap.reference < 0
    || headerMap.customer < 0
    || headerMap.teamMember < 0
    || headerMap.service < 0
    || headerMap.scheduledDate < 0
    || headerMap.slot < 0
    || headerMap.status < 0
  ) {
    return {
      errorKey: 'dataImport.appointments.errors.unsupportedHeaders',
      rows: [],
      headers,
      delimiter,
    };
  }

  const rows = parsed.data
    .slice(1)
    .map((values, index) => buildRowFromValues(values, index + 2, headerMap))
    .filter((row) => row.rawValues.some((value) => normalizeWhitespace(value)));

  if (!rows.length) {
    return { errorKey: 'dataImport.appointments.errors.emptyFile', rows: [], headers, delimiter };
  }

  return { errorKey: null, rows, headers, delimiter };
};

export const buildImportAppointmentsPayload = (rows) => ({
  appointments: rows
    .filter((row) => !row.skipped)
    .map((row) => ({
      freshaAppointmentReference: row.freshaAppointmentReference,
      customerName: row.customerName,
      customerId: row.useWalkInCustomer ? null : (row.resolvedCustomerId || null),
      useWalkInCustomer: Boolean(row.useWalkInCustomer),
      teamMemberName: row.teamMemberName,
      teamMemberId: row.resolvedTeamMemberId || null,
      serviceName: row.serviceName,
      serviceId: row.resolvedServiceId || null,
      categoryName: row.categoryName || null,
      status: row.status,
      scheduledDate: row.scheduledDateRaw || row.scheduledDate,
      periodFrom: row.periodFrom,
      periodTo: row.periodTo,
      unitPrice: row.unitPrice,
      cancellationReason: row.cancellationReason || null,
    })),
});

export const buildFailingAppointmentsCsv = (failedRows, headers, delimiter = ',') => {
  const lines = [Papa.unparse([headers], { delimiter })];
  failedRows.forEach((row) => {
    lines.push(Papa.unparse([row.rawValues || []], { delimiter }));
  });
  return lines.join('\n');
};

export const groupAppointmentRowsByReference = (rows) => {
  const groups = new Map();
  rows.forEach((row) => {
    const key = collapseSpaces(row.freshaAppointmentReference).toUpperCase();
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(row);
  });
  return groups;
};
